"use client"

import { useEffect, useMemo, useRef, useState } from "react"
import { ArrowUpRight, ArrowDownRight } from "lucide-react"
import { apiUrl, authHeaders } from "@/lib/api"
import Sparkline from "./sparkline"

interface ProductLite {
  created_at?: string
  store?: { name?: string } | null
  discount_percentage?: number | string
}

interface StoreLite {
  enabled: boolean
  detected_today?: number
}

interface ScraperLite {
  status: "online" | "error" | "idle" | "warning"
}

interface Stats {
  detectadosHoje: number | null
  detectadosOntem: number | null
  cuponsAtivos: number | null
  cuponsNovosHoje: number | null
  promocoesTotal: number | null
  descontoMedio: number | null
  lojasAtivas: number | null
  lojasTotal: number | null
  totalProdutos: number | null
  scrapersAtivos: number | null
  scrapersTotal: number | null
  hourly: number[]
  ultimaAtualizacao: Date | null
}

const INITIAL: Stats = {
  detectadosHoje: null, detectadosOntem: null,
  cuponsAtivos: null, cuponsNovosHoje: null,
  promocoesTotal: null, descontoMedio: null,
  lojasAtivas: null, lojasTotal: null,
  totalProdutos: null,
  scrapersAtivos: null, scrapersTotal: null,
  hourly: [], ultimaAtualizacao: null,
}

function formatNumber(n: number | null, opts?: { compact?: boolean }): string {
  if (n === null) return "—"
  if (opts?.compact && n >= 1000) {
    const k = n / 1000
    return `${k.toFixed(k >= 10 ? 0 : 1)}K`
  }
  return n.toLocaleString("pt-BR")
}

function timeSince(date: Date | null): string {
  if (!date) return ""
  const sec = Math.floor((Date.now() - date.getTime()) / 1000)
  if (sec < 60) return "agora mesmo"
  const min = Math.floor(sec / 60)
  if (min < 60) return `há ${min} min`
  return `há ${Math.floor(min / 60)}h`
}

function buildHourlySeries(products: ProductLite[]): number[] {
  const buckets = new Array(24).fill(0)
  const now = new Date()
  const cutoff = now.getTime() - 24 * 60 * 60 * 1000
  for (const p of products) {
    if (!p.created_at) continue
    const t = new Date(p.created_at).getTime()
    if (t < cutoff) continue
    const hoursAgo = Math.floor((now.getTime() - t) / (60 * 60 * 1000))
    const idx = 23 - hoursAgo
    if (idx >= 0 && idx < 24) buckets[idx]++
  }
  return buckets
}

function countToday(products: ProductLite[]): number {
  const now = new Date()
  const start = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime()
  return products.filter((p) => p.created_at && new Date(p.created_at).getTime() >= start).length
}

function countYesterday(products: ProductLite[]): number {
  const now = new Date()
  const startToday = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime()
  const startYesterday = startToday - 24 * 60 * 60 * 1000
  return products.filter((p) => {
    if (!p.created_at) return false
    const t = new Date(p.created_at).getTime()
    return t >= startYesterday && t < startToday
  }).length
}

function useCountUp(target: number | null, duration = 1200): number {
  const [value, setValue] = useState(0)
  const fromRef = useRef(0)

  useEffect(() => {
    if (target === null) return
    const safeTarget: number = target
    const from = fromRef.current
    const diff = safeTarget - from
    if (diff === 0) {
      setValue(safeTarget)
      return
    }
    const start = performance.now()
    let raf = 0
    function tick(now: number) {
      const elapsed = now - start
      const t = Math.min(elapsed / duration, 1)
      // easeOutExpo
      const eased = t === 1 ? 1 : 1 - Math.pow(2, -10 * t)
      const v = Math.round(from + diff * eased)
      setValue(v)
      if (t < 1) raf = requestAnimationFrame(tick)
      else fromRef.current = safeTarget
    }
    raf = requestAnimationFrame(tick)
    return () => cancelAnimationFrame(raf)
  }, [target, duration])

  return value
}

interface SecondaryTile {
  label: string
  value: string
  context: string
  trend?: "up" | "down" | "neutral"
}

export default function OverviewStats() {
  const [stats, setStats] = useState<Stats>(INITIAL)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const token = typeof window !== "undefined" ? localStorage.getItem("access_token") : null
    const headers = authHeaders(token)
    setLoading(true)

    Promise.allSettled([
      fetch(`${apiUrl()}/products/?page_size=50&ordering=-discount_percentage`, { headers }).then((r) => r.json()),
      fetch(`${apiUrl()}/products/?page_size=1`, { headers }).then((r) => r.json()),
      fetch(`${apiUrl()}/coupons/?status=ativo&page_size=1`, { headers }).then((r) => r.json()),
      fetch(`${apiUrl()}/promotions/?page_size=1`, { headers }).then((r) => r.json()),
      fetch(`${apiUrl()}/stores/`, { headers }).then((r) => r.json()),
      fetch(`${apiUrl()}/products/?ordering=-created_at&page_size=500`, { headers }).then((r) => r.json()),
      fetch(`${apiUrl()}/coupons/?ordering=-created_at&page_size=100`, { headers }).then((r) => r.json()),
      fetch(`${apiUrl()}/system/health/`, { headers }).then((r) => r.json()).catch(() => null),
    ]).then(([topR, countR, cuponsR, promosR, storesR, recentR, recentCouponsR, healthR]) => {
      const next: Stats = { ...INITIAL, ultimaAtualizacao: new Date() }

      if (topR.status === "fulfilled") {
        const lista = (topR.value?.results || (Array.isArray(topR.value) ? topR.value : [])) as ProductLite[]
        const comDesconto = lista.filter((p) => parseFloat(String(p.discount_percentage || 0)) > 0)
        if (comDesconto.length > 0) {
          next.descontoMedio = Math.round(
            comDesconto.reduce((acc, p) => acc + parseFloat(String(p.discount_percentage || 0)), 0) / comDesconto.length
          )
        }
      }

      if (countR.status === "fulfilled") next.totalProdutos = countR.value?.count ?? 0
      if (cuponsR.status === "fulfilled") next.cuponsAtivos = cuponsR.value?.count ?? 0
      if (promosR.status === "fulfilled") next.promocoesTotal = promosR.value?.count ?? 0

      if (storesR.status === "fulfilled") {
        const lista = (storesR.value?.results || (Array.isArray(storesR.value) ? storesR.value : [])) as StoreLite[]
        next.lojasAtivas = lista.filter((s) => s.enabled).length
        next.lojasTotal = lista.length
        next.detectadosHoje = lista.reduce((acc, s) => acc + (s.detected_today || 0), 0)
      }

      if (recentR.status === "fulfilled") {
        const products = (recentR.value?.results || (Array.isArray(recentR.value) ? recentR.value : [])) as ProductLite[]
        next.hourly = buildHourlySeries(products)
        next.detectadosOntem = countYesterday(products)
        if (next.detectadosHoje === null || next.detectadosHoje === 0) {
          next.detectadosHoje = countToday(products)
        }
      }

      if (recentCouponsR.status === "fulfilled") {
        const coupons = (recentCouponsR.value?.results || []) as { created_at?: string }[]
        next.cuponsNovosHoje = countToday(coupons as ProductLite[])
      }

      if (healthR.status === "fulfilled" && healthR.value?.scrapers) {
        const list = healthR.value.scrapers as ScraperLite[]
        next.scrapersAtivos = list.filter((s) => s.status === "online").length
        next.scrapersTotal = list.length
      }

      setStats(next)
      setLoading(false)
    })
  }, [])

  const heroCount = useCountUp(stats.detectadosHoje)

  const delta = stats.detectadosHoje !== null && stats.detectadosOntem !== null
    ? stats.detectadosHoje - stats.detectadosOntem
    : null
  const deltaPct = delta !== null && stats.detectadosOntem && stats.detectadosOntem > 0
    ? Math.round((delta / stats.detectadosOntem) * 100)
    : null

  const allSystemsOk = stats.scrapersTotal !== null
    && stats.scrapersAtivos === stats.scrapersTotal
    && (stats.scrapersTotal ?? 0) > 0

  const statusLabel = stats.scrapersTotal === null
    ? "Aguardando leitura"
    : allSystemsOk
      ? `${stats.scrapersAtivos} scrapers operacionais`
      : `${stats.scrapersAtivos}/${stats.scrapersTotal} scrapers ativos`

  const secondary: SecondaryTile[] = [
    {
      label: "Cupons ativos",
      value: formatNumber(stats.cuponsAtivos),
      context: stats.cuponsNovosHoje !== null && stats.cuponsNovosHoje > 0 ? `+${stats.cuponsNovosHoje} hoje` : "sem novidades",
      trend: stats.cuponsNovosHoje && stats.cuponsNovosHoje > 0 ? "up" : "neutral",
    },
    {
      label: "Promoções no banco",
      value: formatNumber(stats.promocoesTotal),
      context: "histórico completo",
    },
    {
      label: "Desconto médio",
      value: stats.descontoMedio === null ? "—" : `${stats.descontoMedio}%`,
      context: "top 50 capturados",
    },
    {
      label: "Lojas ativas",
      value: stats.lojasAtivas === null ? "—" : `${stats.lojasAtivas}/${stats.lojasTotal ?? "—"}`,
      context: "scrapers habilitados",
    },
    {
      label: "Catálogo",
      value: formatNumber(stats.totalProdutos, { compact: true }),
      context: "produtos no banco",
    },
  ]

  return (
    <section className="relative overflow-hidden rounded-2xl border border-border/60 bg-gradient-to-br from-background to-background">
      {/* Animated mesh gradient blobs — brand âmbar */}
      <div className="pointer-events-none absolute inset-0 overflow-hidden">
        <div
          aria-hidden
          className="animate-blob-a absolute -top-32 -right-24 h-[460px] w-[460px] rounded-full opacity-70 blur-3xl"
          style={{ background: "radial-gradient(circle, rgba(200,130,26,0.22), transparent 65%)" }}
        />
        <div
          aria-hidden
          className="animate-blob-b absolute -bottom-32 -left-24 h-[360px] w-[360px] rounded-full opacity-55 blur-3xl"
          style={{ background: "radial-gradient(circle, rgba(220,170,60,0.14), transparent 65%)" }}
        />
        <div
          aria-hidden
          className="absolute inset-0 opacity-[0.02]"
          style={{
            backgroundImage:
              "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='160' height='160'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='2'/></filter><rect width='100%' height='100%' filter='url(%23n)'/></svg>\")",
          }}
        />
      </div>

      <div className="relative px-6 py-8 sm:px-10 sm:py-12">
        {/* Status pill row */}
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="inline-flex items-center gap-2 rounded-full border border-border/60 bg-background/70 px-3 py-1 text-xs backdrop-blur-sm">
            <span className="relative flex h-1.5 w-1.5">
              {allSystemsOk && (
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-500 opacity-60" />
              )}
              <span className={`relative inline-flex h-1.5 w-1.5 rounded-full ${allSystemsOk ? "bg-emerald-500" : "bg-amber-500"}`} />
            </span>
            <span className="font-medium text-foreground">{statusLabel}</span>
            <span className="text-border">·</span>
            <span className="text-muted-foreground">{stats.lojasAtivas ?? "—"}/{stats.lojasTotal ?? "—"} lojas</span>
          </div>

          <span className="font-mono text-[11px] text-muted-foreground">
            {loading ? "carregando…" : `atualizado ${timeSince(stats.ultimaAtualizacao)}`}
          </span>
        </div>

        {/* Hero number + sparkline */}
        <div className="mt-8 flex flex-col gap-8 sm:flex-row sm:items-end sm:justify-between">
          <div className="min-w-0">
            <div className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground">
              Detectados hoje
            </div>
            <div className="mt-2 flex flex-wrap items-end gap-x-6 gap-y-2">
              {loading ? (
                <div className="h-20 w-56 animate-pulse rounded bg-muted/60" />
              ) : (
                <span className="font-semibold tracking-[-0.04em] text-foreground tabular-nums text-[clamp(3.75rem,9vw,6.5rem)] leading-[0.85]">
                  {formatNumber(heroCount)}
                </span>
              )}

              {!loading && delta !== null && (
                <span
                  className={`inline-flex items-center gap-1.5 text-sm ${
                    delta > 0 ? "text-emerald-600 dark:text-emerald-400"
                    : delta < 0 ? "text-rose-600 dark:text-rose-400"
                    : "text-muted-foreground"
                  }`}
                >
                  {delta > 0 ? <ArrowUpRight className="h-3.5 w-3.5" /> : delta < 0 ? <ArrowDownRight className="h-3.5 w-3.5" /> : null}
                  <span className="font-mono font-medium">{delta > 0 ? "+" : ""}{delta.toLocaleString("pt-BR")}</span>
                  {deltaPct !== null && (
                    <span className="font-mono text-muted-foreground">
                      ({delta > 0 ? "+" : ""}{deltaPct}%)
                    </span>
                  )}
                  <span className="text-muted-foreground">vs ontem</span>
                </span>
              )}
            </div>
            <p className="mt-3 max-w-md text-sm text-muted-foreground">
              Produtos novos coletados pelos scrapers desde 00h00 (BRT).
            </p>
          </div>

          <div className="flex flex-col items-start gap-2 sm:items-end">
            <span className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
              últimas 24h
            </span>
            {loading ? (
              <div className="h-11 w-[132px] animate-pulse rounded bg-muted/60" />
            ) : (
              <Sparkline data={stats.hourly} height={44} width={132} color="var(--brand-amber)" animateDraw />
            )}
            <span className="font-mono text-[11px] text-muted-foreground">
              pico {Math.max(...stats.hourly, 0)}/h
            </span>
          </div>
        </div>

        {/* Secondary tiles */}
        <div className="mt-10 grid grid-cols-2 gap-px overflow-hidden rounded-xl border border-border/60 bg-border/60 sm:grid-cols-3 lg:grid-cols-5">
          {secondary.map((tile, i) => (
            <div
              key={tile.label}
              className="group animate-stagger-in relative flex flex-col gap-1.5 bg-background/80 px-4 py-4 transition-colors hover:bg-background sm:px-5 sm:py-5"
              style={{ ["--stagger-delay" as string]: `${i * 70}ms` }}
            >
              <span className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground">
                {tile.label}
              </span>
              {loading ? (
                <div className="h-7 w-16 animate-pulse rounded bg-muted/60" />
              ) : (
                <span className="text-2xl font-semibold tracking-tight text-foreground tabular-nums sm:text-[1.75rem]">
                  {tile.value}
                </span>
              )}
              <span
                className={`inline-flex items-center gap-1 text-xs ${
                  tile.trend === "up" ? "text-emerald-600 dark:text-emerald-400"
                  : tile.trend === "down" ? "text-rose-600 dark:text-rose-400"
                  : "text-muted-foreground"
                }`}
              >
                {tile.trend === "up" && <ArrowUpRight className="h-3 w-3" />}
                {tile.trend === "down" && <ArrowDownRight className="h-3 w-3" />}
                {tile.context}
              </span>
            </div>
          ))}
        </div>
      </div>
    </section>
  )
}
